import React from 'react';
import Flexbox from 'flexbox-react';
import { css } from 'emotion';
import TransformationParameter from 'components/core/fields/transformation_parameters/TransformationParameter';
import MessageBox from 'components/core/MessageBox';
import OverrideTable from 'components/core/fields/transformation_parameters/OverrideTable';
import RIENumber from 'components/core/editable/RIENumber';
import InfoBoxLabel from 'components/core/InfoBoxLabel';
import { colorTextGrey } from 'styles/base';

export default class GoodValuesParamter extends TransformationParameter {
    static defaultProps = {
        parameterNames: ['goodfrac_ind_min', 'goodfrac_bad_min'],
        isOverridable: true,
        isDefaultValueVisibleInitially: false,
        isOverrideVisibleInitially: true,
        infoBoxText: '',
    };

    renderMessageBox = () => {
        const divisionStyles = css`border-bottom: 1px solid ${colorTextGrey}; margin-bottom: 2px; padding-bottom: 2px;`;
        return (
            <MessageBox>
                <Flexbox flexDirection={'column'} className={css`width:100%`}>
                    <div>
                        The data transformation libraries calculate the fraction of values considered to be good as:
                    </div>
                    <Flexbox flexDirection={'row'} className={css`justify-content:center; padding:10px; text-align:center;`}>
                        <Flexbox flexDirection={'column'}>
                            <div className={divisionStyles}>
                                # of input values used to compute output data point
                            </div>
                            <div>
                                total possible # of input values to compute output data point
                            </div>
                        </Flexbox>
                    </Flexbox>
                    <div>
                        This fraction is computed for use as a QC threshold.
                    </div>
                </Flexbox>
            </MessageBox>
        );
    };

    renderQuestionText = () => {
        return 'Would you like to set a QC threshold for the fraction of ‘good’ values by dimension?';
    };

    renderOverrideQuestionText = () => {
        return 'Would you like to set a QC threshold for the fraction of ‘good’ values by variable?';
    };

    renderDefaultComponent = () => {
        const indValue = this.getCoordinateSystemDefaultValue('goodfrac_ind_min');
        const badValue = this.getCoordinateSystemDefaultValue('goodfrac_bad_min');

        return (
            <Flexbox flexDirection={'column'} className={css`width:100%;padding-left:8px;`}>
                <Flexbox flexDirection={'row'} alignItems={'center'}>
                    <InfoBoxLabel
                        alignTop
                        className="noshrink"
                        label="Indeterminate Limit"
                        message={''}
                    />
                    <RIENumber
                        value={indValue}
                        change={this.onDefaultValueChange}
                        fieldPath={[]}
                        onChangeData={{parameterName: 'goodfrac_ind_min'}}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        isDisabled={!this.props.hasWritePermissions}
                    />
                </Flexbox>
                <Flexbox flexDirection={'row'} alignItems={'center'}>
                    <InfoBoxLabel
                        alignTop
                        className="noshrink"
                        label="Bad Value"
                        message={''}
                    />
                    <RIENumber
                        value={badValue}
                        change={this.onDefaultValueChange}
                        fieldPath={[]}
                        onChangeData={{parameterName: 'goodfrac_bad_min'}}
                        processId={this.props.process.name}
                        processErrors={this.props.processErrors}
                        isDisabled={!this.props.hasWritePermissions}
                    />
                </Flexbox>
            </Flexbox>
        );
    };

    renderOverrideTable = () => {
        return (
            <GoodValuesOverrideTable
                process={this.props.process}
                processErrors={this.props.processErrors}
                coordinateSystemName={this.props.coordinateSystemName}
                dimensionName={this.props.dimensionName}
                parameterNames={this.props.parameterNames}
                hasWritePermissions={this.props.hasWritePermissions}
            />
        );
    }
}

class GoodValuesOverrideTable extends OverrideTable {

    renderIndQC = (data) => {
        const parameterName = 'goodfrac_ind_min';
        const onChangeData = Object.assign({}, data, { parameterName });
        const value = this.getValue(data, parameterName);

        return (
            <RIENumber
                value={value}
                change={this.onValueChange}
                fieldPath={this.getFieldPath(parameterName, data)}
                onChangeData={onChangeData}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                formDisplay
                isDisabled={!this.props.hasWritePermissions}
                editProps={{ step: '.1' }}
            />
        );
    };

    renderBadQC = (data) => {
        const parameterName = 'goodfrac_bad_min';
        const onChangeData = Object.assign({}, data, { parameterName });
        const value = this.getValue(data, parameterName);

        return (
            <RIENumber
                value={value}
                change={this.onValueChange}
                fieldPath={this.getFieldPath(parameterName, data)}
                onChangeData={onChangeData}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                formDisplay
                isDisabled={!this.props.hasWritePermissions}
                editProps={{ step: '.1' }}
            />
        );
    };

    renderHeader = (labelText, descriptionText) => {
        const labelStyle = css`
            font-weight: 700;
        `;
        const descStyle = css`
            font-size: 11px;
            color: ${colorTextGrey};
            font-weight: normal;
            text-align: center;
        `;
        return (
            <Flexbox flexDirection="column" alignItems="center">
                <div className={labelStyle}>{labelText}</div>
                <div className={descStyle}>{descriptionText}</div>
            </Flexbox>
        );
    };

    getAdditionalColumns() {
        return ([
            {
                property: 'data',
                header: {
                    label: 'Indeterminate Limit',
                    formatters: [
                        data => {
                            return this.renderHeader('Indeterminate Limit',
                                '(data points with good_frac below this limit will be marked as indeterminate)');
                        }
                    ],
                    props: {
                        style: {
                            width: '25%',
                            minWidth: '246px'
                        }
                    }
                },
                cell: {
                    formatters: [
                        data => { return this.renderIndQC(data); }
                    ]
                }
            },
            {
                property: 'data',
                header: {
                    label: 'Bad Limit',
                    formatters: [
                        data => {
                            return this.renderHeader('Bad Limit',
                                '(data points with good_frac below this limit will be marked as bad)');
                        }
                    ],
                    props: {
                        style: {
                            width: '25%',
                            minWidth: '246px'
                        }
                    }
                },
                cell: {
                    formatters: [
                        data => { return this.renderBadQC(data); }
                    ]
                }
            }
        ]);
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/GoodValuesParameter.js